import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, Button, Badge, PageTemplate, LoadingSpinner, Alert } from '../components/ui';
import { FiActivity, FiClock, FiUser, FiFilter, FiRefreshCw } from 'react-icons/fi';

const ActivityLog = () => {
    const { currentUser } = useAuth();
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filter, setFilter] = useState('all');

    useEffect(() => {
        fetchLogs();
    }, []);

    const fetchLogs = async () => {
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch('/api/admin/booking-logs?limit=50', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error('Failed to fetch activity logs');
            }

            const data = await response.json();
            setLogs(data);
        } catch (err) {
            console.error('Error fetching logs:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString();
    };

    const getActionColor = (action) => {
        const colors = {
            'created': 'info',
            'confirmed': 'success',
            'cancelled': 'error',
            'rejected': 'error',
            'completed': 'success',
            'returned': 'primary',
            'pending': 'warning'
        };
        return colors[action] || 'neutral';
    };

    if (currentUser?.User_Role !== 'ATL_ADMIN') {
        return (
            <PageTemplate title="Activity Log" description="System activity and status changes">
                <Alert variant="error">Access Denied. Admin privileges required.</Alert>
            </PageTemplate>
        );
    }

    return (
        <PageTemplate 
            title="Activity Log" 
            description="Track system status changes and user activities"
            icon="📋"
            headerActions={
                <Button onClick={fetchLogs} variant="outline" icon={FiRefreshCw}>
                    Refresh
                </Button>
            }
        >
            <Card>
                <Card.Content>
                    {error && <Alert variant="error" className="mb-4">{error}</Alert>}
                    
                    {loading ? (
                        <div className="flex justify-center py-12">
                            <LoadingSpinner />
                        </div>
                    ) : logs.length === 0 ? (
                        <div className="text-center py-12 text-gray-500">
                            No activity logs found.
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-left">
                                <thead>
                                    <tr className="border-b border-gray-200 dark:border-gray-700">
                                        <th className="py-3 px-4 font-serif font-medium text-gray-700 dark:text-gray-300">Time</th>
                                        <th className="py-3 px-4 font-serif font-medium text-gray-700 dark:text-gray-300">User</th>
                                        <th className="py-3 px-4 font-serif font-medium text-gray-700 dark:text-gray-300">Action</th>
                                        <th className="py-3 px-4 font-serif font-medium text-gray-700 dark:text-gray-300">Details</th>
                                        <th className="py-3 px-4 font-serif font-medium text-gray-700 dark:text-gray-300">Admin</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                                    {logs.map((log) => (
                                        <tr key={log._id} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                                            <td className="py-3 px-4 text-sm whitespace-nowrap">
                                                <div className="flex items-center text-gray-600 dark:text-gray-400">
                                                    <FiClock className="mr-2" />
                                                    {formatDate(log.status_changed_at || log.createdAt)}
                                                </div>
                                            </td>
                                            <td className="py-3 px-4 text-sm">
                                                <div className="flex items-center font-medium text-gray-900 dark:text-white">
                                                    <FiUser className="mr-2 text-gray-400" />
                                                    {log.user?.full_name || log.user_id || 'Unknown User'}
                                                </div>
                                            </td>
                                            <td className="py-3 px-4">
                                                <Badge variant={getActionColor(log.action)}>
                                                    {log.action?.toUpperCase()}
                                                </Badge>
                                            </td>
                                            <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                                                <div className="max-w-md">
                                                    {log.booking_type === 'equipment' && (
                                                        <span>Equipment: {log.equipment?.eqm_name || log.booking_details?.equipment_name || 'N/A'}</span>
                                                    )}
                                                    {log.booking_type === 'venue' && (
                                                        <span>Venue: {log.venue?.venue_unit || 'N/A'}</span>
                                                    )}
                                                    {log.notes && (
                                                        <div className="text-xs text-gray-500 mt-1 italic">
                                                            Note: {log.notes}
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="py-3 px-4 text-sm text-gray-500">
                                                {log.admin?.full_name || log.admin_id || '-'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Card.Content>
            </Card>
        </PageTemplate>
    );
};

export default ActivityLog;
